import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { Info, AlertTriangle, XCircle, Sun, Moon, ChevronDown, ChevronUp, X, LockKeyhole, Pin, CircleCheck } from 'lucide-react';

const Alert = ({
  variant = 'primary',
  children,
  dismissible = false,
  expandable = false,
  title,
  icon,
  onClose,
  className = '',
  size = 'xs',
  iconSize = 4,
  textWrapperClass = '',
  forceCenter = false,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const variants = {
    primary: 'bg-blue-50 border-blue-500 text-blue-800',
    secondary: 'bg-gray-50 border-gray-200 text-gray-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    danger: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    light: 'bg-gray-50 border-gray-200 text-gray-600',
    dark: 'bg-gray-800 border-gray-700 text-gray-100',
  };

  const defaultIcons = {
    primary: LockKeyhole,
    secondary: Pin,
    success: CircleCheck,
    danger: XCircle,
    warning: AlertTriangle,
    info: Info,
    light: Sun,
    dark: Moon,
  };

  const iconBgColors = {
    primary: 'bg-blue-500',
    secondary: 'bg-gray-500',
    success: 'bg-transparent',
    danger: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
    light: 'bg-gray-400',
    dark: 'bg-gray-700',
  };

  const textColors = {
    dark: 'text-gray-300',
    danger: 'text-red-800',
    success: 'text-green-800',
  };

  const iconColors = {
    success: 'text-green-500',
  };

  const iconSizes = {
    success: '6',
  };

  const titleColors = {
    dark: 'text-gray-100',
  };

  const IconComponent = icon || defaultIcons[variant] || Info;

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  if (!isVisible) return null;

  return (
    <div
      className={`
        relative p-4 border rounded-md
        ${variants[variant] || variants.info}
        ${className}
      `}
      role="alert"
      {...props}
    >
      <div className={`flex items-${title ? 'start' : 'center'} gap-3 ${forceCenter ? 'justify-center' : ''}`}>
        <div className={`flex items-center justify-center w-${iconSize * 2} h-${iconSize * 2} text-${iconColors[variant] || 'white'} rounded-full flex-shrink-0 mt-0.5 ${iconBgColors[variant] || iconBgColors.info}`}>
          <IconComponent className={`w-${iconSizes[variant] || iconSize} h-${iconSizes[variant] || iconSize}`} />
        </div>
        <div className={`flex-1 ${textWrapperClass}`}>
          {title && (
            <h4 className={`text-sm font-semibold ${titleColors[variant] || 'text-gray-800'} mb-1`}>{title}</h4>
          )}
          {expandable ? (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-end justify-between text-left group w-full"
            >
              <div className={`flex-1 text-xs ${textColors[variant] || 'text-gray-600'} leading-relaxed ${!isExpanded ? 'truncate-1' : ''}`}>
                {children}
              </div>
              <div className="flex-shrink-0 ml-2 opacity-60 group-hover:opacity-80 transition-opacity">
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </button>
          ) : (
            <div className={`text-${size} ${textColors[variant] || 'text-gray-600'} leading-relaxed`}>{children}</div>
          )}
        </div>
      </div>
      {dismissible && (
        <button
          type="button"
          className="absolute top-2 right-2 p-1 hover:opacity-70"
          onClick={handleClose}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

Alert.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']),
  children: PropTypes.node.isRequired,
  dismissible: PropTypes.bool,
  expandable: PropTypes.bool,
  title: PropTypes.string,
  icon: PropTypes.elementType,
  onClose: PropTypes.func,
  className: PropTypes.string,
  size: PropTypes.string,
  iconSize: PropTypes.number,
  textWrapperClass: PropTypes.string,
  forceCenter: PropTypes.bool,
};

Alert.defaultProps = {
  variant: 'primary',
  dismissible: false,
  expandable: false,
  title: null,
  icon: null,
  onClose: null,
  className: '',
  size: 'xs',
  iconSize: 4,
  textWrapperClass: '',
  forceCenter: false,
};

export default Alert;
